import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

/**
 * Política de privacidad pública.
 *
 * Cumple el deber de información de la Ley 21.719 (vigente desde el 01-12-2026):
 * qué datos se recogen, con qué finalidad, con qué base de licitud, cuánto se
 * conservan y cómo ejercer los derechos ARCOP.
 *
 * Los datos de contacto salen de /api/public/config para que no haya que
 * editar este archivo cada vez que cambia el correo o el teléfono del local.
 */
export default function Privacidad() {
  const [local, setLocal] = useState({ nombre: 'el restaurante', email: '', telefono: '', direccion: '' });
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    const API = import.meta.env.VITE_BACKEND_URL ||
      'https://southamerica-east1-cocina-19123.cloudfunctions.net/api';
    fetch(`${API}/api/public/config`)
      .then(r => r.json())
      .then(d => { if (d?.ok && d.config) setLocal(l => ({ ...l, ...d.config })); })
      .catch(() => {});

    // Respaldo: si no hay correo ni teléfono en la ficha del local, el canal
    // para ejercer derechos es el WhatsApp publicado en redes.
    getDoc(doc(db, 'config', 'redes_sociales'))
      .then(s => { if (s.exists()) setWhatsapp(s.data().whatsapp || ''); })
      .catch(() => {});
  }, []);

  const contacto = local.email || local.telefono ||
    (whatsapp ? `nuestro WhatsApp +${whatsapp.replace(/\D/g, '')}` : 'los canales de contacto publicados en nuestro menú');

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/menu" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6">
          <ArrowLeft size={16} /> Volver al menú
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7 space-y-6">
          <div className="flex items-center gap-3 border-b border-slate-100 pb-5">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center">
              <ShieldCheck className="text-emerald-600" size={22} />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-900">Cómo tratamos tus datos</h1>
              <p className="text-sm text-slate-500">{local.nombre}</p>
            </div>
          </div>

          <Seccion titulo="Qué datos pedimos y para qué">
            <Dato campo="Nombre y teléfono" uso="Identificar tu pedido y avisarte cuando esté listo." />
            <Dato campo="Dirección y ubicación" uso="Solo si eliges delivery, para llevarte el pedido." />
            <Dato campo="Detalle del pedido" uso="Prepararlo, cobrarlo y emitir el documento tributario." />
            <Dato campo="Historial de compras" uso="Solo si te sumas al programa de puntos, para calcularlos." />
            <p className="text-sm text-slate-600 pt-1">
              No pedimos RUT, correo ni datos de pago en la web: el pago se hace al recibir el
              pedido o por transferencia directa.
            </p>
          </Seccion>

          <Seccion titulo="Por qué podemos tratarlos">
            <p className="text-sm text-slate-600">
              Porque son necesarios para cumplir con el pedido que nos haces, y para las
              obligaciones tributarias que nos exige la ley. Para el programa de puntos y
              cualquier comunicación promocional pedimos tu consentimiento aparte, y lo puedes
              retirar cuando quieras.
            </p>
          </Seccion>

          <Seccion titulo="Cuánto tiempo los guardamos">
            <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-5">
              <li>Nombre, teléfono y dirección del pedido: <strong>12 meses</strong>, luego se
                borran del registro y solo queda el dato de venta sin identificarte.</li>
              <li>Registros de venta: <strong>6 años</strong>, porque el Servicio de Impuestos
                Internos lo exige.</li>
              <li>Programa de puntos: mientras seas parte. Si te retiras, se elimina.</li>
            </ul>
          </Seccion>

          <Seccion titulo="Con quién los compartimos">
            <p className="text-sm text-slate-600">
              Con nadie con fines comerciales. Solo con los servicios que hacen funcionar la
              aplicación (Google Firebase, que aloja los datos) y, cuando corresponde, con el
              Servicio de Impuestos Internos.
            </p>
          </Seccion>

          <Seccion titulo="Tus derechos">
            <p className="text-sm text-slate-600 mb-2">
              Puedes pedirnos en cualquier momento que te mostremos qué datos tuyos tenemos,
              que los corrijamos si están mal, que los borremos, que dejemos de usarlos, o que
              te los entreguemos en un archivo para llevártelos a otra parte.
            </p>
            <p className="text-sm text-slate-600">
              Escríbenos a <strong className="text-slate-800">{contacto}</strong> indicando qué
              necesitas. Te respondemos dentro de 30 días corridos. Si no quedas conforme,
              puedes reclamar ante la Agencia de Protección de Datos Personales.
            </p>
          </Seccion>

          <Seccion titulo="Si ocurre un incidente">
            <p className="text-sm text-slate-600">
              Si alguna vez tus datos quedaran expuestos, te avisamos a ti y a la Agencia de
              Protección de Datos Personales sin demora, explicando qué pasó y qué hicimos.
            </p>
          </Seccion>

          <p className="text-xs text-slate-400 border-t border-slate-100 pt-5">
            Última actualización: 18 de agosto de 2026 · Ley 19.628 y Ley 21.719 sobre
            protección de datos personales.
            {local.direccion ? ` · ${local.direccion}` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}

const Seccion = ({ titulo, children }) => (
  <section className="space-y-2">
    <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{titulo}</h2>
    {children}
  </section>
);

const Dato = ({ campo, uso }) => (
  <div className="flex gap-2 text-sm">
    <span className="font-semibold text-slate-700 shrink-0">{campo}:</span>
    <span className="text-slate-600">{uso}</span>
  </div>
);
